// Global cache for getActualUrl API to prevent duplicate calls
class UrlCache {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.hasCalledActualUrl = false;
    this.actualUrlPromise = null;
  }

  // Generate cache key based on current URL
  getCacheKey() {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }

  // Get cached result or make API call
  async getActualUrl(apiCall) {
    const key = 'actualUrl';
    
    // Prevent multiple calls - if already called, return cached or pending result
    if (this.hasCalledActualUrl) {
      if (this.actualUrlPromise) {
        console.log('[UrlCache] Returning existing actualUrl promise (already called)');
        return this.actualUrlPromise;
      }
      if (this.cache.has(key)) {
        const cached = this.cache.get(key);
        console.log('[UrlCache] Returning cached actualUrl (already called):', cached);
        return cached;
      }
      // If called before but no promise/cache, return empty result
      console.log('[UrlCache] ActualUrl already called but no result, returning empty');
      return { success: false, data: null };
    }
    
    // Mark as called to prevent duplicate calls
    this.hasCalledActualUrl = true;
    
    // Check if we have a pending request for this key
    if (this.pendingRequests.has(key)) {
      console.log('[UrlCache] Returning existing pending request for actualUrl');
      this.actualUrlPromise = this.pendingRequests.get(key);
      return this.actualUrlPromise;
    }
    
    // Check cache first
    if (this.cache.has(key)) {
      const cached = this.cache.get(key);
      console.log('[UrlCache] Returning cached actualUrl:', cached);
      this.actualUrlPromise = Promise.resolve(cached);
      return this.actualUrlPromise;
    }
    
    console.log('[UrlCache] Making fresh API call for actualUrl (first time)');
    
    // Create and cache the promise
    const promise = apiCall().then(result => {
      console.log('[UrlCache] API call completed, caching result:', result);
      this.cache.set(key, result);
      this.pendingRequests.delete(key);
      return result;
    }).catch(error => {
      console.error('[UrlCache] API call failed:', error);
      this.pendingRequests.delete(key);
      // Reset flag on error so it can be retried
      this.hasCalledActualUrl = false;
      this.actualUrlPromise = null;
      throw error;
    });
    
    this.pendingRequests.set(key, promise);
    this.actualUrlPromise = promise;
    return promise;
  }

  // Clear cache for specific URL or all cache
  clearCache(url = null) {
    if (url) {
      this.cache.delete(url);
      this.pendingRequests.delete(url);
    } else {
      this.cache.clear();
      this.pendingRequests.clear();
      this.hasCalledActualUrl = false;
      this.actualUrlPromise = null;
      console.log('[UrlCache] Cache cleared and actualUrl flag reset');
    }
  }

  // Get cache statistics for debugging
  getStats() {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size
    };
  }
}

// Export singleton instance
export const urlCache = new UrlCache();
